/**
 * @license
 * Copyright 2019 Google LLC
 * SPDX-License-Identifier: BSD-3-Clause
 */
const H = globalThis, K = H.ShadowRoot && (H.ShadyCSS === void 0 || H.ShadyCSS.nativeShadow) && "adoptedStyleSheets" in Document.prototype && "replace" in CSSStyleSheet.prototype, lt = Symbol(), Z = /* @__PURE__ */ new WeakMap();
let ft = class {
  constructor(t, e, i) {
    if (this._$cssResult$ = !0, i !== lt) throw Error("CSSResult is not constructable. Use `unsafeCSS` or `css` instead.");
    this.cssText = t, this.t = e;
  }
  get styleSheet() {
    let t = this.o;
    const e = this.t;
    if (K && t === void 0) {
      const i = e !== void 0 && e.length === 1;
      i && (t = Z.get(e)), t === void 0 && ((this.o = t = new CSSStyleSheet()).replaceSync(this.cssText), i && Z.set(e, t));
    }
    return t;
  }
  toString() {
    return this.cssText;
  }
};
const bt = (n) => new ft(typeof n == "string" ? n : n + "", void 0, lt), vt = (n, t) => {
  if (K) n.adoptedStyleSheets = t.map((e) => e instanceof CSSStyleSheet ? e : e.styleSheet);
  else for (const e of t) {
    const i = document.createElement("style"), s = H.litNonce;
    s !== void 0 && i.setAttribute("nonce", s), i.textContent = e.cssText, n.appendChild(i);
  }
}, G = K ? (n) => n : (n) => n instanceof CSSStyleSheet ? ((t) => {
  let e = "";
  for (const i of t.cssRules) e += i.cssText;
  return bt(e);
})(n) : n;
/**
 * @license
 * Copyright 2017 Google LLC
 * SPDX-License-Identifier: BSD-3-Clause
 */
const { is: yt, defineProperty: wt, getOwnPropertyDescriptor: mt, getOwnPropertyNames: gt, getOwnPropertySymbols: $t, getPrototypeOf: xt } = Object, m = globalThis, Q = m.trustedTypes, _t = Q ? Q.emptyScript : "", L = m.reactiveElementPolyfillSupport, C = (n, t) => n, N = { toAttribute(n, t) {
  switch (t) {
    case Boolean:
      n = n ? _t : null;
      break;
    case Object:
    case Array:
      n = n == null ? n : JSON.stringify(n);
  }
  return n;
}, fromAttribute(n, t) {
  let e = n;
  switch (t) {
    case Boolean:
      e = n !== null;
      break;
    case Number:
      e = n === null ? null : Number(n);
      break;
    case Object:
    case Array:
      try {
        e = JSON.parse(n);
      } catch {
        e = null;
      }
  }
  return e;
} }, F = (n, t) => !yt(n, t), X = { attribute: !0, type: String, converter: N, reflect: !1, useDefault: !1, hasChanged: F };
Symbol.metadata ?? (Symbol.metadata = Symbol("metadata")), m.litPropertyMetadata ?? (m.litPropertyMetadata = /* @__PURE__ */ new WeakMap());
class k extends HTMLElement {
  static addInitializer(t) {
    this._$Ei(), (this.l ?? (this.l = [])).push(t);
  }
  static get observedAttributes() {
    return this.finalize(), this._$Eh && [...this._$Eh.keys()];
  }
  static createProperty(t, e = X) {
    if (e.state && (e.attribute = !1), this._$Ei(), this.prototype.hasOwnProperty(t) && ((e = Object.create(e)).wrapped = !0), this.elementProperties.set(t, e), !e.noAccessor) {
      const i = Symbol(), s = this.getPropertyDescriptor(t, i, e);
      s !== void 0 && wt(this.prototype, t, s);
    }
  }
  static getPropertyDescriptor(t, e, i) {
    const { get: s, set: r } = mt(this.prototype, t) ?? { get() {
      return this[e];
    }, set(o) {
      this[e] = o;
    } };
    return { get: s, set(o) {
      const l = s == null ? void 0 : s.call(this);
      r == null || r.call(this, o), this.requestUpdate(t, l, i);
    }, configurable: !0, enumerable: !0 };
  }
  static getPropertyOptions(t) {
    return this.elementProperties.get(t) ?? X;
  }
  static _$Ei() {
    if (this.hasOwnProperty(C("elementProperties"))) return;
    const t = xt(this);
    t.finalize(), t.l !== void 0 && (this.l = [...t.l]), this.elementProperties = new Map(t.elementProperties);
  }
  static finalize() {
    if (this.hasOwnProperty(C("finalized"))) return;
    if (this.finalized = !0, this._$Ei(), this.hasOwnProperty(C("properties"))) {
      const e = this.properties, i = [...gt(e), ...$t(e)];
      for (const s of i) this.createProperty(s, e[s]);
    }
    const t = this[Symbol.metadata];
    if (t !== null) {
      const e = litPropertyMetadata.get(t);
      if (e !== void 0) for (const [i, s] of e) this.elementProperties.set(i, s);
    }
    this._$Eh = /* @__PURE__ */ new Map();
    for (const [e, i] of this.elementProperties) {
      const s = this._$Eu(e, i);
      s !== void 0 && this._$Eh.set(s, e);
    }
    this.elementStyles = this.finalizeStyles(this.styles);
  }
  static finalizeStyles(t) {
    const e = [];
    if (Array.isArray(t)) {
      const i = new Set(t.flat(1 / 0).reverse());
      for (const s of i) e.unshift(G(s));
    } else t !== void 0 && e.push(G(t));
    return e;
  }
  static _$Eu(t, e) {
    const i = e.attribute;
    return i === !1 ? void 0 : typeof i == "string" ? i : typeof t == "string" ? t.toLowerCase() : void 0;
  }
  constructor() {
    super(), this._$Ep = void 0, this.isUpdatePending = !1, this.hasUpdated = !1, this._$Em = null, this._$Ev();
  }
  _$Ev() {
    var t;
    this._$ES = new Promise((e) => this.enableUpdating = e), this._$AL = /* @__PURE__ */ new Map(), this._$E_(), this.requestUpdate(), (t = this.constructor.l) == null || t.forEach((e) => e(this));
  }
  addController(t) {
    var e;
    (this._$EO ?? (this._$EO = /* @__PURE__ */ new Set())).add(t), this.renderRoot !== void 0 && this.isConnected && ((e = t.hostConnected) == null || e.call(t));
  }
  removeController(t) {
    var e;
    (e = this._$EO) == null || e.delete(t);
  }
  _$E_() {
    const t = /* @__PURE__ */ new Map(), e = this.constructor.elementProperties;
    for (const i of e.keys()) this.hasOwnProperty(i) && (t.set(i, this[i]), delete this[i]);
    t.size > 0 && (this._$Ep = t);
  }
  createRenderRoot() {
    const t = this.shadowRoot ?? this.attachShadow(this.constructor.shadowRootOptions);
    return vt(t, this.constructor.elementStyles), t;
  }
  connectedCallback() {
    var t;
    this.renderRoot ?? (this.renderRoot = this.createRenderRoot()), this.enableUpdating(!0), (t = this._$EO) == null || t.forEach((e) => {
      var i;
      return (i = e.hostConnected) == null ? void 0 : i.call(e);
    });
  }
  enableUpdating(t) {
  }
  disconnectedCallback() {
    var t;
    (t = this._$EO) == null || t.forEach((e) => {
      var i;
      return (i = e.hostDisconnected) == null ? void 0 : i.call(e);
    });
  }
  attributeChangedCallback(t, e, i) {
    this._$AK(t, i);
  }
  _$ET(t, e) {
    var r;
    const i = this.constructor.elementProperties.get(t), s = this.constructor._$Eu(t, i);
    if (s !== void 0 && i.reflect === !0) {
      const o = (((r = i.converter) == null ? void 0 : r.toAttribute) !== void 0 ? i.converter : N).toAttribute(e, i.type);
      this._$Em = t, o == null ? this.removeAttribute(s) : this.setAttribute(s, o), this._$Em = null;
    }
  }
  _$AK(t, e) {
    var r, o;
    const i = this.constructor, s = i._$Eh.get(t);
    if (s !== void 0 && this._$Em !== s) {
      const l = i.getPropertyOptions(s), a = typeof l.converter == "function" ? { fromAttribute: l.converter } : ((r = l.converter) == null ? void 0 : r.fromAttribute) !== void 0 ? l.converter : N;
      this._$Em = s, this[s] = a.fromAttribute(e, l.type) ?? ((o = this._$Ej) == null ? void 0 : o.get(s)) ?? null, this._$Em = null;
    }
  }
  requestUpdate(t, e, i) {
    var s;
    if (t !== void 0) {
      const r = this.constructor, o = this[t];
      if (i ?? (i = r.getPropertyOptions(t)), !((i.hasChanged ?? F)(o, e) || i.useDefault && i.reflect && o === ((s = this._$Ej) == null ? void 0 : s.get(t)) && !this.hasAttribute(r._$Eu(t, i)))) return;
      this.C(t, e, i);
    }
    this.isUpdatePending === !1 && (this._$ES = this._$EP());
  }
  C(t, e, { useDefault: i, reflect: s, wrapped: r }, o) {
    i && !(this._$Ej ?? (this._$Ej = /* @__PURE__ */ new Map())).has(t) && (this._$Ej.set(t, o ?? e ?? this[t]), r !== !0 || o !== void 0) || (this._$AL.has(t) || (this.hasUpdated || i || (e = void 0), this._$AL.set(t, e)), s === !0 && this._$Em !== t && (this._$Eq ?? (this._$Eq = /* @__PURE__ */ new Set())).add(t));
  }
  async _$EP() {
    this.isUpdatePending = !0;
    try {
      await this._$ES;
    } catch (e) {
      Promise.reject(e);
    }
    const t = this.scheduleUpdate();
    return t != null && await t, !this.isUpdatePending;
  }
  scheduleUpdate() {
    return this.performUpdate();
  }
  performUpdate() {
    var i;
    if (!this.isUpdatePending) return;
    if (!this.hasUpdated) {
      if (this.renderRoot ?? (this.renderRoot = this.createRenderRoot()), this._$Ep) {
        for (const [r, o] of this._$Ep) this[r] = o;
        this._$Ep = void 0;
      }
      const s = this.constructor.elementProperties;
      if (s.size > 0) for (const [r, o] of s) {
        const { wrapped: l } = o, a = this[r];
        l !== !0 || this._$AL.has(r) || a === void 0 || this.C(r, void 0, o, a);
      }
    }
    let t = !1;
    const e = this._$AL;
    try {
      t = this.shouldUpdate(e), t ? (this.willUpdate(e), (i = this._$EO) == null || i.forEach((s) => {
        var r;
        return (r = s.hostUpdate) == null ? void 0 : r.call(s);
      }), this.update(e)) : this._$EM();
    } catch (s) {
      throw t = !1, this._$EM(), s;
    }
    t && this._$AE(e);
  }
  willUpdate(t) {
  }
  _$AE(t) {
    var e;
    (e = this._$EO) == null || e.forEach((i) => {
      var s;
      return (s = i.hostUpdated) == null ? void 0 : s.call(i);
    }), this.hasUpdated || (this.hasUpdated = !0, this.firstUpdated(t)), this.updated(t);
  }
  _$EM() {
    this._$AL = /* @__PURE__ */ new Map(), this.isUpdatePending = !1;
  }
  get updateComplete() {
    return this.getUpdateComplete();
  }
  getUpdateComplete() {
    return this._$ES;
  }
  shouldUpdate(t) {
    return !0;
  }
  update(t) {
    this._$Eq && (this._$Eq = this._$Eq.forEach((e) => this._$ET(e, this[e]))), this._$EM();
  }
  updated(t) {
  }
  firstUpdated(t) {
  }
}
k.elementStyles = [], k.shadowRootOptions = { mode: "open" }, k[C("elementProperties")] = /* @__PURE__ */ new Map(), k[C("finalized")] = /* @__PURE__ */ new Map(), L == null || L({ ReactiveElement: k }), (m.reactiveElementVersions ?? (m.reactiveElementVersions = [])).push("2.1.0");
/**
 * @license
 * Copyright 2017 Google LLC
 * SPDX-License-Identifier: BSD-3-Clause
 */
const I = globalThis, B = I.trustedTypes, Y = B ? B.createPolicy("lit-html", { createHTML: (n) => n }) : void 0, dt = "$lit$", w = `lit$${Math.random().toFixed(9).slice(2)}$`, ht = "?" + w, kt = `<${ht}>`, _ = document, M = () => _.createComment(""), U = (n) => n === null || typeof n != "object" && typeof n != "function", J = Array.isArray, At = (n) => J(n) || typeof (n == null ? void 0 : n[Symbol.iterator]) == "function", V = `[ 	
\f\r]`, S = /<(?:(!--|\/[^a-zA-Z])|(\/?[a-zA-Z][^>\s]*)|(\/?$))/g, tt = /-->/g, et = />/g, g = RegExp(`>|${V}(?:([^\\s"'>=/]+)(${V}*=${V}*(?:[^ 	
\f\r"'\`<>=]|("|')|))|$)`, "g"), it = /'/g, st = /"/g, pt = /^(?:script|style|textarea|title)$/i, A = Symbol.for("lit-noChange"), f = Symbol.for("lit-nothing"), rt = /* @__PURE__ */ new WeakMap(), $ = _.createTreeWalker(_, 129);
function ct(n, t) {
  if (!J(n) || !n.hasOwnProperty("raw")) throw Error("invalid template strings array");
  return Y !== void 0 ? Y.createHTML(t) : t;
}
const Et = (n, t) => {
  const e = n.length - 1, i = [];
  let s, r = t === 2 ? "<svg>" : t === 3 ? "<math>" : "", o = S;
  for (let l = 0; l < e; l++) {
    const a = n[l];
    let p, b, d = -1, v = 0;
    for (; v < a.length && (o.lastIndex = v, b = o.exec(a), b !== null); ) v = o.lastIndex, o === S ? b[1] === "!--" ? o = tt : b[1] !== void 0 ? o = et : b[2] !== void 0 ? (pt.test(b[2]) && (s = RegExp("</" + b[2], "g")), o = g) : b[3] !== void 0 && (o = g) : o === g ? b[0] === ">" ? (o = s ?? S, d = -1) : b[1] === void 0 ? d = -2 : (d = o.lastIndex - b[2].length, p = b[1], o = b[3] === void 0 ? g : b[3] === '"' ? st : it) : o === st || o === it ? o = g : o === tt || o === et ? o = S : (o = g, s = void 0);
    const y = o === g && n[l + 1].startsWith("/>") ? " " : "";
    r += o === S ? a + kt : d >= 0 ? (i.push(p), a.slice(0, d) + dt + a.slice(d) + w + y) : a + w + (d === -2 ? l : y);
  }
  return [ct(n, r + (n[e] || "<?>") + (t === 2 ? "</svg>" : t === 3 ? "</math>" : "")), i];
};
class T {
  constructor({ strings: t, _$litType$: e }, i) {
    let s;
    this.parts = [];
    let r = 0, o = 0;
    const l = t.length - 1, a = this.parts, [p, b] = Et(t, e);
    if (this.el = T.createElement(p, i), $.currentNode = this.el.content, e === 2 || e === 3) {
      const d = this.el.content.firstChild;
      d.replaceWith(...d.childNodes);
    }
    for (; (s = $.nextNode()) !== null && a.length < l; ) {
      if (s.nodeType === 1) {
        if (s.hasAttributes()) for (const d of s.getAttributeNames()) if (d.endsWith(dt)) {
          const v = b[o++], y = s.getAttribute(d).split(w), R = /([.?@])?(.*)/.exec(v);
          a.push({ type: 1, index: r, name: R[2], strings: y, ctor: R[1] === "." ? Ct : R[1] === "?" ? It : R[1] === "@" ? Pt : D }), s.removeAttribute(d);
        } else d.startsWith(w) && (a.push({ type: 6, index: r }), s.removeAttribute(d));
        if (pt.test(s.tagName)) {
          const d = s.textContent.split(w), v = d.length - 1;
          if (v > 0) {
            s.textContent = B ? B.emptyScript : "";
            for (let y = 0; y < v; y++) s.append(d[y], M()), $.nextNode(), a.push({ type: 2, index: ++r });
            s.append(d[v], M());
          }
        }
      } else if (s.nodeType === 8) if (s.data === ht) a.push({ type: 2, index: r });
      else {
        let d = -1;
        for (; (d = s.data.indexOf(w, d + 1)) !== -1; ) a.push({ type: 7, index: r }), d += w.length - 1;
      }
      r++;
    }
  }
  static createElement(t, e) {
    const i = _.createElement("template");
    return i.innerHTML = t, i;
  }
}
function E(n, t, e = n, i) {
  var o, l;
  if (t === A) return t;
  let s = i !== void 0 ? (o = e._$Co) == null ? void 0 : o[i] : e._$Cl;
  const r = U(t) ? void 0 : t._$litDirective$;
  return (s == null ? void 0 : s.constructor) !== r && ((l = s == null ? void 0 : s._$AO) == null || l.call(s, !1), r === void 0 ? s = void 0 : (s = new r(n), s._$AT(n, e, i)), i !== void 0 ? (e._$Co ?? (e._$Co = []))[i] = s : e._$Cl = s), s !== void 0 && (t = E(n, s._$AS(n, t.values), s, i)), t;
}
class St {
  constructor(t, e) {
    this._$AV = [], this._$AN = void 0, this._$AD = t, this._$AM = e;
  }
  get parentNode() {
    return this._$AM.parentNode;
  }
  get _$AU() {
    return this._$AM._$AU;
  }
  u(t) {
    const { el: { content: e }, parts: i } = this._$AD, s = ((t == null ? void 0 : t.creationScope) ?? _).importNode(e, !0);
    $.currentNode = s;
    let r = $.nextNode(), o = 0, l = 0, a = i[0];
    for (; a !== void 0; ) {
      if (o === a.index) {
        let p;
        a.type === 2 ? p = new O(r, r.nextSibling, this, t) : a.type === 1 ? p = new a.ctor(r, a.name, a.strings, this, t) : a.type === 6 && (p = new Mt(r, this, t)), this._$AV.push(p), a = i[++l];
      }
      o !== (a == null ? void 0 : a.index) && (r = $.nextNode(), o++);
    }
    return $.currentNode = _, s;
  }
  p(t) {
    let e = 0;
    for (const i of this._$AV) i !== void 0 && (i.strings !== void 0 ? (i._$AI(t, i, e), e += i.strings.length - 2) : i._$AI(t[e])), e++;
  }
}
class O {
  get _$AU() {
    var t;
    return ((t = this._$AM) == null ? void 0 : t._$AU) ?? this._$Cv;
  }
  constructor(t, e, i, s) {
    this.type = 2, this._$AH = f, this._$AN = void 0, this._$AA = t, this._$AB = e, this._$AM = i, this.options = s, this._$Cv = (s == null ? void 0 : s.isConnected) ?? !0;
  }
  get parentNode() {
    let t = this._$AA.parentNode;
    const e = this._$AM;
    return e !== void 0 && (t == null ? void 0 : t.nodeType) === 11 && (t = e.parentNode), t;
  }
  get startNode() {
    return this._$AA;
  }
  get endNode() {
    return this._$AB;
  }
  _$AI(t, e = this) {
    t = E(this, t, e), U(t) ? t === f || t == null || t === "" ? (this._$AH !== f && this._$AR(), this._$AH = f) : t !== this._$AH && t !== A && this._(t) : t._$litType$ !== void 0 ? this.$(t) : t.nodeType !== void 0 ? this.T(t) : At(t) ? this.k(t) : this._(t);
  }
  O(t) {
    return this._$AA.parentNode.insertBefore(t, this._$AB);
  }
  T(t) {
    this._$AH !== t && (this._$AR(), this._$AH = this.O(t));
  }
  _(t) {
    this._$AH !== f && U(this._$AH) ? this._$AA.nextSibling.data = t : this.T(_.createTextNode(t)), this._$AH = t;
  }
  $(t) {
    var r;
    const { values: e, _$litType$: i } = t, s = typeof i == "number" ? this._$AC(t) : (i.el === void 0 && (i.el = T.createElement(ct(i.h, i.h[0]), this.options)), i);
    if (((r = this._$AH) == null ? void 0 : r._$AD) === s) this._$AH.p(e);
    else {
      const o = new St(s, this), l = o.u(this.options);
      o.p(e), this.T(l), this._$AH = o;
    }
  }
  _$AC(t) {
    let e = rt.get(t.strings);
    return e === void 0 && rt.set(t.strings, e = new T(t)), e;
  }
  k(t) {
    J(this._$AH) || (this._$AH = [], this._$AR());
    const e = this._$AH;
    let i, s = 0;
    for (const r of t) s === e.length ? e.push(i = new O(this.O(M()), this.O(M()), this, this.options)) : i = e[s], i._$AI(r), s++;
    s < e.length && (this._$AR(i && i._$AB.nextSibling, s), e.length = s);
  }
  _$AR(t = this._$AA.nextSibling, e) {
    var i;
    for ((i = this._$AP) == null ? void 0 : i.call(this, !1, !0, e); t && t !== this._$AB; ) {
      const s = t.nextSibling;
      t.remove(), t = s;
    }
  }
  setConnected(t) {
    var e;
    this._$AM === void 0 && (this._$Cv = t, (e = this._$AP) == null || e.call(this, t));
  }
}
class D {
  get tagName() {
    return this.element.tagName;
  }
  get _$AU() {
    return this._$AM._$AU;
  }
  constructor(t, e, i, s, r) {
    this.type = 1, this._$AH = f, this._$AN = void 0, this.element = t, this.name = e, this._$AM = s, this.options = r, i.length > 2 || i[0] !== "" || i[1] !== "" ? (this._$AH = Array(i.length - 1).fill(new String()), this.strings = i) : this._$AH = f;
  }
  _$AI(t, e = this, i, s) {
    const r = this.strings;
    let o = !1;
    if (r === void 0) t = E(this, t, e, 0), o = !U(t) || t !== this._$AH && t !== A, o && (this._$AH = t);
    else {
      const l = t;
      let a, p;
      for (t = r[0], a = 0; a < r.length - 1; a++) p = E(this, l[i + a], e, a), p === A && (p = this._$AH[a]), o || (o = !U(p) || p !== this._$AH[a]), p === f ? t = f : t !== f && (t += (p ?? "") + r[a + 1]), this._$AH[a] = p;
    }
    o && !s && this.j(t);
  }
  j(t) {
    t === f ? this.element.removeAttribute(this.name) : this.element.setAttribute(this.name, t ?? "");
  }
}
class Ct extends D {
  constructor() {
    super(...arguments), this.type = 3;
  }
  j(t) {
    this.element[this.name] = t === f ? void 0 : t;
  }
}
class It extends D {
  constructor() {
    super(...arguments), this.type = 4;
  }
  j(t) {
    this.element.toggleAttribute(this.name, !!t && t !== f);
  }
}
class Pt extends D {
  constructor(t, e, i, s, r) {
    super(t, e, i, s, r), this.type = 5;
  }
  _$AI(t, e = this) {
    if ((t = E(this, t, e, 0) ?? f) === A) return;
    const i = this._$AH, s = t === f && i !== f || t.capture !== i.capture || t.once !== i.once || t.passive !== i.passive, r = t !== f && (i === f || s);
    s && this.element.removeEventListener(this.name, this, i), r && this.element.addEventListener(this.name, this, t), this._$AH = t;
  }
  handleEvent(t) {
    var e;
    typeof this._$AH == "function" ? this._$AH.call(((e = this.options) == null ? void 0 : e.host) ?? this.element, t) : this._$AH.handleEvent(t);
  }
}
class Mt {
  constructor(t, e, i) {
    this.element = t, this.type = 6, this._$AN = void 0, this._$AM = e, this.options = i;
  }
  get _$AU() {
    return this._$AM._$AU;
  }
  _$AI(t) {
    E(this, t);
  }
}
const q = I.litHtmlPolyfillSupport;
q == null || q(T, O), (I.litHtmlVersions ?? (I.litHtmlVersions = [])).push("3.3.0");
const Ut = (n, t, e) => {
  const i = (e == null ? void 0 : e.renderBefore) ?? t;
  let s = i._$litPart$;
  if (s === void 0) {
    const r = (e == null ? void 0 : e.renderBefore) ?? null;
    i._$litPart$ = s = new O(t.insertBefore(M(), r), r, void 0, e ?? {});
  }
  return s._$AI(n), s;
};
/**
 * @license
 * Copyright 2017 Google LLC
 * SPDX-License-Identifier: BSD-3-Clause
 */
const x = globalThis;
class P extends k {
  constructor() {
    super(...arguments), this.renderOptions = { host: this }, this._$Do = void 0;
  }
  createRenderRoot() {
    var e;
    const t = super.createRenderRoot();
    return (e = this.renderOptions).renderBefore ?? (e.renderBefore = t.firstChild), t;
  }
  update(t) {
    const e = this.render();
    this.hasUpdated || (this.renderOptions.isConnected = this.isConnected), super.update(t), this._$Do = Ut(e, this.renderRoot, this.renderOptions);
  }
  connectedCallback() {
    var t;
    super.connectedCallback(), (t = this._$Do) == null || t.setConnected(!0);
  }
  disconnectedCallback() {
    var t;
    super.disconnectedCallback(), (t = this._$Do) == null || t.setConnected(!1);
  }
  render() {
    return A;
  }
}
var at;
P._$litElement$ = !0, P.finalized = !0, (at = x.litElementHydrateSupport) == null || at.call(x, { LitElement: P });
const j = x.litElementPolyfillSupport;
j == null || j({ LitElement: P });
(x.litElementVersions ?? (x.litElementVersions = [])).push("4.2.0");
/**
 * @license
 * Copyright 2017 Google LLC
 * SPDX-License-Identifier: BSD-3-Clause
 */
const Tt = { attribute: !0, type: String, converter: N, reflect: !1, hasChanged: F }, Ot = (n = Tt, t, e) => {
  const { kind: i, metadata: s } = e;
  let r = globalThis.litPropertyMetadata.get(s);
  if (r === void 0 && globalThis.litPropertyMetadata.set(s, r = /* @__PURE__ */ new Map()), i === "setter" && ((n = Object.create(n)).wrapped = !0), r.set(e.name, n), i === "accessor") {
    const { name: o } = e;
    return { set(l) {
      const a = t.get.call(this);
      t.set.call(this, l), this.requestUpdate(o, a, n);
    }, init(l) {
      return l !== void 0 && this.C(o, void 0, n, l), l;
    } };
  }
  if (i === "setter") {
    const { name: o } = e;
    return function(l) {
      const a = this[o];
      t.call(this, l), this.requestUpdate(o, a, n);
    };
  }
  throw Error("Unsupported decorator location: " + i);
};
function c(n) {
  return (t, e) => typeof e == "object" ? Ot(n, t, e) : ((i, s, r) => {
    const o = s.hasOwnProperty(r);
    return s.constructor.createProperty(r, i), o ? Object.getOwnPropertyDescriptor(s, r) : void 0;
  })(n, t, e);
}
const Rt = (n) => {
  class t extends n {
    constructor(...i) {
      super(...i), this._attachDeclarativeTemplate();
    }
    firstUpdated(i) {
      super.firstUpdated(i), this.updateAttributes(), this.setupEventHandling();
    }
    attributeChangedCallback(i, s, r) {
      super.attributeChangedCallback(i, s, r), this.updateAttributes();
    }
    /**
     * Attempts to use a declarative shadow DOM template if provided.
     * If used, this bypasses Lit's standard rendering.
     */
    _attachDeclarativeTemplate() {
      const i = this.constructor;
      if (!i.template) return;
      const s = document.createElement("template");
      s.innerHTML = i.template.trim();
      const r = s.content.firstChild;
      if (r && r.nodeName === "TEMPLATE" && r.getAttribute("shadowrootmode") === "open") {
        const o = r, l = this.attachShadow({ mode: "open" }), a = o.content.cloneNode(!0);
        l.appendChild(a), this.__hasDeclarativeShadowRoot = !0;
      }
    }
    updateAttributes() {
      throw new Error("updateAttributes is not implemented in your component");
    }
    setupEventHandling() {
      throw new Error("setupEventHandling is not implemented in your component");
    }
    /**
     * Override createRenderRoot to prevent Lit's standard rendering
     * if we've already attached a declarative shadow DOM template
     */
    createRenderRoot() {
      return this.__hasDeclarativeShadowRoot ? this.shadowRoot : super.createRenderRoot();
    }
  }
  return t;
}, Ht = (n) => {
  class t extends n {
    connectedCallback() {
      super.connectedCallback(), this._bindDeclarativeEvents();
    }
    /**
     * Finds all elements with @event attributes and binds them to the corresponding methods
     */
    _bindDeclarativeEvents() {
      const i = this.shadowRoot;
      if (!i) return;
      const s = document.createTreeWalker(
        i,
        NodeFilter.SHOW_ELEMENT
      );
      let r = s.nextNode();
      for (; r; )
        Array.from(r.attributes).forEach((o) => {
          if (o.name.startsWith("@")) {
            const l = o.name.slice(1), a = o.value, p = this[a];
            typeof p == "function" ? r.addEventListener(l, p.bind(this)) : console.warn(
              `${this.localName}: no method '${a}' for event '${l}'`
            );
          }
        }), r = s.nextNode();
    }
  }
  return t;
}, Nt = (n) => Ht(Rt(n));
let nt = 0;
const W = (n) => (nt += 1, `${n}-${nt}`), Bt = '<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M4.66675 11.9998l5.99995 5.3333L19.3334 6.66644" stroke="currentColor" stroke-width="1.5" stroke-miterlimit="10" stroke-linecap="square"/></svg>', zt = '<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M12 7.29317V13.3296m0 1.4436v1.3333M21.3339 12c0 5.1549-4.1789 9.3339-9.3339 9.3339-5.15495.0-9.33386-4.179-9.33386-9.3339.0-5.15495 4.17891-9.33386 9.33386-9.33386 5.155.0 9.3339 4.17891 9.3339 9.33386z" stroke="currentColor" stroke-width="1.5"/></svg>';
var Dt = Object.defineProperty, u = (n, t, e, i) => {
  for (var s = void 0, r = n.length - 1, o; r >= 0; r--)
    (o = n[r]) && (s = o(t, e, s) || s);
  return s && Dt(t, e, s), s;
};
const ut = `<template shadowrootmode="open"><style>/*! tailwindcss v4.1.11 | MIT License | https://tailwindcss.com */
@layer properties{@supports ((-webkit-hyphens:none) and (not (margin-trim:inline))) or ((-moz-orient:inline) and (not (color:rgb(from red r g b)))){*,:before,:after,::backdrop{--tw-rotate-x:initial;--tw-rotate-y:initial;--tw-rotate-z:initial;--tw-skew-x:initial;--tw-skew-y:initial;--tw-shadow:0 0 #0000;--tw-shadow-color:initial;--tw-shadow-alpha:100%;--tw-inset-shadow:0 0 #0000;--tw-inset-shadow-color:initial;--tw-inset-shadow-alpha:100%;--tw-ring-color:initial;--tw-ring-shadow:0 0 #0000;--tw-inset-ring-color:initial;--tw-inset-ring-shadow:0 0 #0000;--tw-ring-inset:initial;--tw-ring-offset-width:0px;--tw-ring-offset-color:#fff;--tw-ring-offset-shadow:0 0 #0000;--tw-blur:initial;--tw-brightness:initial;--tw-contrast:initial;--tw-grayscale:initial;--tw-hue-rotate:initial;--tw-invert:initial;--tw-opacity:initial;--tw-saturate:initial;--tw-sepia:initial;--tw-drop-shadow:initial;--tw-drop-shadow-color:initial;--tw-drop-shadow-alpha:100%;--tw-drop-shadow-size:initial;--tw-border-style:solid;--tw-outline-style:solid}}}.sr-only{clip:rect(0,0,0,0);white-space:nowrap;border-width:0;width:1px;height:1px;margin:-1px;padding:0;position:absolute;overflow:hidden}.static{position:static}.isolate{isolation:isolate}.m-0{margin:var(--bdk-space-0,0rem)}.mb-2{margin-bottom:var(--bdk-space-2,1.333rem)}.ml-8{margin-left:var(--bdk-space-8,5.333rem)}.block{display:block}.contents{display:contents}.flex{display:flex}.hidden{display:none}.inline{display:inline}.h-full{height:100%}.w-full{width:100%}.max-w-\\[500px\\]{max-width:500px}.transform{transform:var(--tw-rotate-x,)var(--tw-rotate-y,)var(--tw-rotate-z,)var(--tw-skew-x,)var(--tw-skew-y,)}.flex-col{flex-direction:column}.items-start{align-items:flex-start}.justify-center{justify-content:center}.gap-2{gap:var(--bdk-space-2,1.333rem)}.gap-4{gap:var(--bdk-space-4,2.667rem)}.overflow-y-auto{overflow-y:auto}.p-0{padding:var(--bdk-space-0,0rem)}.bdk-tw-text-field:has(.bdk-text-field--icons) input{padding-right:var(--bdk-space-11,7.333rem)}.bdk-tw-text-field:has(*+.bdk-text-field--status-icon) input{padding-right:var(--bdk-space-17,11.333rem)}.bdk-tw-strong-l{letter-spacing:.1rem;text-transform:inherit;font-family:Eduard Breuninger;font-size:3.333rem;font-weight:500;line-height:5rem}.bdk-tw-text-field:has(input:disabled) .bdk-text-field--hint{color:var(--bdk-pebble,#e0dfde);outline-color:var(--bdk-pebble,#e0dfde)}.bdk-tw-text-field[valid] .bdk-text-field--hint,.bdk-tw-text-field[valid] .bdk-text-field--status-icon{color:var(--bdk-success,#136233)}.bdk-tw-text-field[invalid] .bdk-text-field--status-icon{color:var(--bdk-error,#b20c16)}.bdk-tw-text-field:has(input:disabled) .bdk-text-field--status-icon{color:var(--bdk-pebble,#e0dfde)}.shadow{--tw-shadow:0 1px 3px 0 var(--tw-shadow-color,#0000001a),0 1px 2px -1px var(--tw-shadow-color,#0000001a);box-shadow:var(--tw-inset-shadow),var(--tw-inset-ring-shadow),var(--tw-ring-offset-shadow),var(--tw-ring-shadow),var(--tw-shadow)}.blur{--tw-blur:blur(8px);filter:var(--tw-blur,)var(--tw-brightness,)var(--tw-contrast,)var(--tw-grayscale,)var(--tw-hue-rotate,)var(--tw-invert,)var(--tw-saturate,)var(--tw-sepia,)var(--tw-drop-shadow,)}.filter{filter:var(--tw-blur,)var(--tw-brightness,)var(--tw-contrast,)var(--tw-grayscale,)var(--tw-hue-rotate,)var(--tw-invert,)var(--tw-saturate,)var(--tw-sepia,)var(--tw-drop-shadow,)}:host{box-sizing:border-box;width:100%;display:block}:host([hidden]){display:none}:host([inverted][invalid]) .bdk-text-field{margin-inline:calc(var(--bdk-space-2,1.333rem)*-1);border-radius:var(--bdk-radius-2,1.333rem);background-color:var(--bdk-error-background,#ffedf4);padding:var(--bdk-space-2,1.333rem)}.bdk-text-field--container{align-items:center;gap:var(--bdk-space-2,1.333rem);display:flex;position:relative}.bdk-text-field--label{margin-bottom:var(--bdk-space-1,.667rem);align-items:center;gap:var(--bdk-space-1,.667rem);letter-spacing:.067rem;text-transform:inherit;color:var(--bdk-coal-100,#1a1a1a);font-family:Eduard Breuninger;font-size:2.667rem;font-weight:400;line-height:4rem;display:flex}:host(:focus-within) .bdk-text-field--label{letter-spacing:.067rem;text-transform:inherit;font-family:Eduard Breuninger;font-size:2.667rem;font-weight:500;line-height:4rem}:host(:disabled) .bdk-text-field--label{color:var(--bdk-pebble,#e0dfde);outline-color:var(--bdk-pebble,#e0dfde)}:host(:disabled[inverted]) .bdk-text-field--label{color:var(--bdk-snow-50,#ffffff80)}:host([inverted]) .bdk-text-field--label{color:var(--bdk-snow-100,#fff)}:host([valid]) .bdk-text-field--label{color:var(--bdk-success,#136233)}:host([invalid]) .bdk-text-field--label{color:var(--bdk-error,#b20c16)}:host([hidden-label]) .bdk-text-field--label{clip:rect(0,0,0,0);white-space:nowrap;border-width:0;width:1px;height:1px;margin:-1px;padding:0;position:absolute;overflow:hidden}:host([valid][inverted]) .bdk-text-field--label{color:var(--bdk-snow-100,#fff)}.bdk-text-field--input{height:var(--bdk-space-14,9.333rem);border-radius:var(--bdk-radius-2,1.333rem);border-style:var(--tw-border-style);width:100%;padding-inline:var(--bdk-space-3,2rem);letter-spacing:.067rem;text-transform:inherit;color:var(--bdk-coal-100,#1a1a1a);outline-style:var(--tw-outline-style);outline-offset:calc(2px*-1);outline-width:1px;outline-color:var(--bdk-smoke,#928d8b);--tw-outline-style:solid;border-width:0;outline-style:solid;font-family:Eduard Breuninger;font-size:2.667rem;font-weight:500;line-height:4rem}.bdk-text-field--input::-moz-placeholder{letter-spacing:.067rem;text-transform:inherit;color:var(--bdk-ash,#6c6765);font-family:Eduard Breuninger;font-size:2.667rem;font-weight:400;line-height:4rem}.bdk-text-field--input::placeholder{letter-spacing:.067rem;text-transform:inherit;color:var(--bdk-ash,#6c6765);font-family:Eduard Breuninger;font-size:2.667rem;font-weight:400;line-height:4rem}.bdk-text-field--input:not(:disabled):where(:hover,:focus,:active){outline-style:var(--tw-outline-style);outline-width:2px;outline-color:var(--bdk-coal-100,#1a1a1a);--tw-outline-style:solid;outline-style:solid}.bdk-text-field--input:disabled{background-color:var(--bdk-snow-100,#fff);color:var(--bdk-pebble,#e0dfde);outline-color:var(--bdk-pebble,#e0dfde)}.bdk-text-field--input:disabled::-moz-placeholder{color:var(--bdk-pebble,#e0dfde)}.bdk-text-field--input:disabled::placeholder{color:var(--bdk-pebble,#e0dfde)}:host([inverted]) .bdk-text-field--input{outline-offset:calc(1px*-1)}:host([valid]) .bdk-text-field--input{padding-right:var(--bdk-space-11,7.333rem);outline-color:var(--bdk-success,#136233)}:host([invalid]) .bdk-text-field--input{padding-right:var(--bdk-space-11,7.333rem);outline-color:var(--bdk-error,#b20c16)}.bdk-text-field--container:has(*+.bdk-text-field--status-icon) .bdk-text-field--input{padding-right:var(--bdk-space-17,11.333rem)}.bdk-text-field--icons{right:var(--bdk-space-3,2rem);align-items:center;gap:var(--bdk-space-1,.667rem);display:flex;position:absolute}.bdk-text-field--status-icon{aspect-ratio:1;height:var(--bdk-space-6,4rem)}:host([valid]) .bdk-text-field--status-icon{color:var(--bdk-success,#136233)}:host([invalid]) .bdk-text-field--status-icon{color:var(--bdk-error,#b20c16)}:host(:disabled) .bdk-text-field--status-icon{color:var(--bdk-pebble,#e0dfde)}.bdk-text-field--error,.bdk-text-field--hint{margin-top:var(--bdk-space-1,.667rem);letter-spacing:.067rem;text-transform:inherit;font-family:Eduard Breuninger;font-size:2.667rem;font-weight:500;line-height:4rem}.bdk-text-field--error{color:var(--bdk-error,#b20c16)}.bdk-text-field--hint{letter-spacing:.067rem;text-transform:inherit;color:var(--bdk-coal-100,#1a1a1a);font-family:Eduard Breuninger;font-size:2.667rem;font-weight:500;line-height:4rem}:host([inverted]) .bdk-text-field--hint{color:var(--bdk-snow-100,#fff)}:host([valid]) .bdk-text-field--hint{color:var(--bdk-success,#136233)}:host([valid][inverted]) .bdk-text-field--hint{color:var(--bdk-snow-100,#fff)}:host(:disabled) .bdk-text-field--hint{color:var(--bdk-pebble,#e0dfde)}:host(:disabled[inverted]) .bdk-text-field--hint{color:var(--bdk-snow-50,#ffffff80)}@property --tw-rotate-x{syntax:"*";inherits:false}@property --tw-rotate-y{syntax:"*";inherits:false}@property --tw-rotate-z{syntax:"*";inherits:false}@property --tw-skew-x{syntax:"*";inherits:false}@property --tw-skew-y{syntax:"*";inherits:false}@property --tw-shadow{syntax:"*";inherits:false;initial-value:0 0 #0000}@property --tw-shadow-color{syntax:"*";inherits:false}@property --tw-shadow-alpha{syntax:"<percentage>";inherits:false;initial-value:100%}@property --tw-inset-shadow{syntax:"*";inherits:false;initial-value:0 0 #0000}@property --tw-inset-shadow-color{syntax:"*";inherits:false}@property --tw-inset-shadow-alpha{syntax:"<percentage>";inherits:false;initial-value:100%}@property --tw-ring-color{syntax:"*";inherits:false}@property --tw-ring-shadow{syntax:"*";inherits:false;initial-value:0 0 #0000}@property --tw-inset-ring-color{syntax:"*";inherits:false}@property --tw-inset-ring-shadow{syntax:"*";inherits:false;initial-value:0 0 #0000}@property --tw-ring-inset{syntax:"*";inherits:false}@property --tw-ring-offset-width{syntax:"<length>";inherits:false;initial-value:0}@property --tw-ring-offset-color{syntax:"*";inherits:false;initial-value:#fff}@property --tw-ring-offset-shadow{syntax:"*";inherits:false;initial-value:0 0 #0000}@property --tw-blur{syntax:"*";inherits:false}@property --tw-brightness{syntax:"*";inherits:false}@property --tw-contrast{syntax:"*";inherits:false}@property --tw-grayscale{syntax:"*";inherits:false}@property --tw-hue-rotate{syntax:"*";inherits:false}@property --tw-invert{syntax:"*";inherits:false}@property --tw-opacity{syntax:"*";inherits:false}@property --tw-saturate{syntax:"*";inherits:false}@property --tw-sepia{syntax:"*";inherits:false}@property --tw-drop-shadow{syntax:"*";inherits:false}@property --tw-drop-shadow-color{syntax:"*";inherits:false}@property --tw-drop-shadow-alpha{syntax:"<percentage>";inherits:false;initial-value:100%}@property --tw-drop-shadow-size{syntax:"*";inherits:false}@property --tw-border-style{syntax:"*";inherits:false;initial-value:solid}@property --tw-outline-style{syntax:"*";inherits:false;initial-value:solid}
</style>
  
  <div class="bdk-text-field">
    <label class="bdk-text-field--label">
      <slot></slot>
    </label>

    <div class="bdk-text-field--container">
      <input class="bdk-text-field--input" type="text" />
      <div class="bdk-text-field--icons">
        <slot name="custom-icon"></slot>
      </div>
    </div>

    <div class="bdk-text-field--hint">
      <slot name="hint"></slot>
    </div>

    <div class="bdk-text-field--error" aria-live="assertive">
      <slot name="error"></slot>
    </div>
  </div>
</template>`;
function Vt() {
  return ut;
}
const ot = {
  check24: Bt,
  warning24: zt
}, z = class z extends Nt(P) {
  constructor() {
    super(...arguments), this.internals = this.attachInternals(), this.onKeyUp = (t) => {
      const { form: e } = this.internals;
      e && t.key === "Enter" && e.requestSubmit();
    }, this.hiddenLabel = !1, this.inputId = W("bdk-text-field-input"), this.hintId = W("bdk-text-field-hint"), this.errorId = W("bdk-text-field-error"), this.disabled = !1, this.required = !1, this.readonly = !1, this.spellcheck = !1, this.type = "text", this.value = "";
  }
  get form() {
    return this.internals.form;
  }
  /* Getter seems to work after connecting, @query does not */
  get input() {
    var t;
    return (t = this.shadowRoot) == null ? void 0 : t.querySelector("input");
  }
  get label() {
    var t;
    return (t = this.shadowRoot) == null ? void 0 : t.querySelector("label");
  }
  get hint() {
    var t;
    return (t = this.shadowRoot) == null ? void 0 : t.querySelector(".bdk-text-field--hint");
  }
  get error() {
    var t;
    return (t = this.shadowRoot) == null ? void 0 : t.querySelector(".bdk-text-field--error");
  }
  firstUpdated(t) {
    super.firstUpdated(t), this.input && (this.internals.setFormValue && this.internals.setFormValue(this.value), this.internals.setValidity && this.internals.setValidity(
      this.input.validity,
      this.input.validationMessage,
      this.input
    ));
  }
  update(t) {
    super.update(t), this.input && (t.has("value") && (this.input.value = this.value, this.internals.setValidity && this.internals.setValidity(
      this.input.validity,
      this.input.validationMessage,
      this.input
    )), t.has("type") && this.internals.setValidity && this.internals.setValidity(
      this.input.validity,
      this.input.validationMessage,
      this.input
    ));
  }
  updateAttributes() {
    var t, e;
    if (this.type && ((t = this.input) == null || t.setAttribute("type", this.type ?? "text")), this.value && ((e = this.input) == null || e.setAttribute("value", this.value)), this._setAttributeIfExists("autocomplete", this.autocomplete), this._setBooleanAttributeIfTrue("disabled", this.disabled), this._setAttributeIfExists("min", this.min), this._setAttributeIfExists("max", this.max), this._setAttributeIfExists("minlength", this.minlength), this._setAttributeIfExists("maxlength", this.maxlength), this._setAttributeIfExists("pattern", this.pattern), this._setAttributeIfExists("placeholder", this.placeholder), this._setAttributeIfExists("inputmode", this.inputmode), this._setBooleanAttributeIfTrue("required", this.required), this._setBooleanAttributeIfTrue("readonly", this.readonly), this._setBooleanAttributeIfTrue("spellcheck", this.spellcheck), this.setIds(), this.invalid && this.valid)
      throw new Error("Cannot set both invalid and valid attributes");
    this.unsetValidationState(), this.valid && this.handleValidState(), this.invalid && this.handleInvalidState();
  }
  _setAttributeIfExists(t, e) {
    var i, s;
    e ? (i = this.input) == null || i.setAttribute(t, e) : (s = this.input) == null || s.removeAttribute(t);
  }
  _setBooleanAttributeIfTrue(t, e) {
    var i, s;
    e ? (i = this.input) == null || i.setAttribute(t, "") : (s = this.input) == null || s.removeAttribute(t);
  }
  setupEventHandling() {
    this.input && (this.input.addEventListener("input", () => {
      this.handleInput();
    }), this.input.addEventListener("change", (t) => {
      this.handleChangeEvent(t);
    }), this.input.addEventListener("keyup", this.onKeyUp));
  }
  setIds() {
    var t, e, i, s;
    this.inputId && ((t = this.input) == null || t.setAttribute("id", this.inputId)), this.inputId && ((e = this.label) == null || e.setAttribute("for", this.inputId)), this.hintId && ((i = this.hint) == null || i.setAttribute("id", this.hintId), (s = this.input) == null || s.setAttribute("aria-describedby", this.hintId));
  }
  unsetValidationState() {
    var t, e, i, s, r;
    (t = this.error) == null || t.setAttribute("hidden", ""), (e = this.hint) == null || e.removeAttribute("hidden"), this.removeStatusIcon(), (i = this.input) == null || i.removeAttribute("aria-invalid"), this.hintId && this.hint ? (s = this.input) == null || s.setAttribute("aria-describedby", this.hintId) : (r = this.input) == null || r.removeAttribute("aria-describedby");
  }
  handleInvalidState() {
    var e, i, s, r, o, l;
    const t = (e = this.input) == null ? void 0 : e.getAttribute("aria-describedby");
    (i = this.hint) == null || i.setAttribute("hidden", ""), (s = this.error) == null || s.removeAttribute("hidden"), (r = this.input) == null || r.setAttribute("aria-invalid", "true"), this.addStatusIcon(ot.warning24), (o = this.input) == null || o.setAttribute(
      "aria-describedby",
      [t, this.errorId].join(" ")
    ), (l = this.error) == null || l.setAttribute("id", this.errorId);
  }
  handleValidState() {
    this.addStatusIcon(ot.check24);
  }
  addStatusIcon(t) {
    var i;
    const e = document.createElement("span");
    e.className = "bdk-text-field--status-icon", e.innerHTML = t, (i = this.shadowRoot.querySelector(".bdk-text-field--icons")) == null || i.appendChild(
      e
    );
  }
  removeStatusIcon() {
    var t;
    (t = this.shadowRoot.querySelector(".bdk-text-field--status-icon")) == null || t.remove();
  }
  /* Sets the input value on the <bdk-text-field> via internals for form association and executes browser validation */
  handleInput() {
    if (!this.input) return;
    const { value: t } = this.input;
    this.value = t, this.internals.setFormValue && this.internals.setFormValue(t), this.internals.setValidity && this.internals.setValidity(
      this.input.validity,
      this.input.validationMessage,
      this.input
    );
  }
  focusInput() {
    this.input && this.input.focus();
  }
  /* Needed for search fields with custom buttons */
  resetInput(t) {
    if (t.preventDefault(), t.stopPropagation(), !this.input) return;
    this.input.value = "", this.input.focus();
    const e = new InputEvent("input", {
      bubbles: !0,
      composed: !0
    });
    this.input.dispatchEvent(e);
  }
  /* Change event is not bubbling outside the shadow root */
  handleChangeEvent(t) {
    if (t.preventDefault(), t.stopPropagation(), !this.input) return;
    const e = new Event("change", { bubbles: !0, composed: !0 });
    this.dispatchEvent(e);
  }
};
z.formAssociated = !0, z.template = ut;
let h = z;
u([
  c({ type: Boolean, reflect: !0 })
], h.prototype, "valid");
u([
  c({ type: Boolean, reflect: !0 })
], h.prototype, "invalid");
u([
  c({ type: Boolean, reflect: !0 })
], h.prototype, "inverted");
u([
  c({ type: Boolean, reflect: !0 })
], h.prototype, "hiddenLabel");
u([
  c({ type: String, attribute: "input-id" })
], h.prototype, "inputId");
u([
  c({ type: String, attribute: "hint-id" })
], h.prototype, "hintId");
u([
  c({ type: String, attribute: "error-id" })
], h.prototype, "errorId");
u([
  c()
], h.prototype, "autocomplete");
u([
  c({ type: Boolean })
], h.prototype, "disabled");
u([
  c()
], h.prototype, "min");
u([
  c()
], h.prototype, "max");
u([
  c()
], h.prototype, "minlength");
u([
  c()
], h.prototype, "maxlength");
u([
  c()
], h.prototype, "pattern");
u([
  c()
], h.prototype, "placeholder");
u([
  c()
], h.prototype, "inputmode");
u([
  c({ type: Boolean })
], h.prototype, "required");
u([
  c({ type: Boolean })
], h.prototype, "readonly");
u([
  c({ type: Boolean })
], h.prototype, "spellcheck");
u([
  c()
], h.prototype, "type");
u([
  c()
], h.prototype, "value");
export {
  h as BdkTextField,
  ut as default,
  Vt as dsd
};
